import React, { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl" | "full"; // more granular
  closeOnOverlayClick?: boolean;
}

const sizeClasses = {
  sm: "max-w-sm w-full",
  md: "max-w-md w-full",
  lg: "max-w-2xl w-full",
  xl: "max-w-4xl w-full",
  full: "w-full h-full sm:max-w-none sm:h-auto",
};

/**
 * Modal - Industry-standard modal with Tailwind
 * Best practices:
 * - Full accessibility (a11y) with focus trap and ARIA
 * - Responsive design with mobile-first approach
 * - Smooth animations with proper timing
 * - Overlay click to close (configurable)
 * - Focus management and restoration
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showCloseButton = true,
  className = "",
  size = "lg",
  closeOnOverlayClick = true,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastFocusedElement = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle open/close animation
  useEffect(() => {
    if (isOpen) {
      lastFocusedElement.current = document.activeElement as HTMLElement;
      setShouldRender(true);
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Focus trap + ESC + body scroll prevention
  useEffect(() => {
    if (!isOpen) return;

    const bodyStyle = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Removed keyboard event handlers for perfect focus stability

    const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    firstFocusable?.focus();

    return () => {
      document.body.style.overflow = bodyStyle;
      // Removed keyboard event handler cleanup - no more focus interference
      lastFocusedElement.current?.focus();
    };
  }, [isOpen, onClose]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-[99999] flex items-center justify-center p-4 
        transition-colors duration-300 ${isVisible ? "bg-black/50" : "bg-black/0"}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
      aria-describedby="modal-content"
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <div
        ref={dialogRef}
        className={`bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden 
          flex flex-col max-h-[95vh] transform transition-all duration-300 
          ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"} 
          ${sizeClasses[size]} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="px-6 py-4 flex items-center justify-between bg-red-600">
            {title && (
              <h3 id="modal-title" className="text-lg font-semibold text-white truncate">
                {title}
              </h3>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 
                  flex items-center justify-center transition-colors shadow-md"
                aria-label="Close modal"
              >
                <IoClose className="text-white text-lg" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div id="modal-content" className="flex-1 overflow-auto p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
